import { useId } from "react";
import type { FieldErrors, UseFormRegister } from "react-hook-form";
import { Trash2Icon } from "lucide-react";

import { FormFieldShell } from "@/components/forms/FormField";
import { FormInput } from "@/components/forms/FormInput";
import { FormTextarea } from "@/components/forms/FormTextarea";
import { Heading, TextLink } from "@/components/typography";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FieldGroup } from "@/components/ui/field";
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from "@/components/ui/input-group";
import type { CampaignType } from "../../../shared/types";
import type { CampaignFormValues } from "../campaign";

function optionalPrice(value: string): number | undefined {
    if (value === "") {
        return undefined;
    }
    const number = Number(value);
    return Number.isNaN(number) ? undefined : number;
}

function priceIsOptionalOrNonNegative(value: number | undefined) {
    return value === undefined || value >= 0 || "Price can’t be negative.";
}

const SOUND_URL_PATTERN =
    /^(https:\/\/www\.tiktok\.com\/music\/[A-Za-zÀ-ÖØ-öø-ÿ0-9-']*[0-9]|https:\/\/vm\.tiktok\.com\/[A-Za-zÀ-ÖØ-öø-ÿ0-9-]*|https:\/\/www\.instagram\.com\/reels\/audio\/[A-Za-zÀ-ÖØ-öø-ÿ0-9-]*|https:\/\/www\.youtube\.com\/source\/*|https:\/\/youtube\.com\/source\/*)/;

type BriefSectionProps = {
    campaignType: CampaignType;
    errors: FieldErrors<CampaignFormValues>;
    register: UseFormRegister<CampaignFormValues>;
    currencySymbol: string;
    fileUrl?: string;
    onReplaceFile: () => void;
};

/** Sound URL when the type is Song, optional min and max price, deliverables, details, and the optional brief file. */
export function BriefSection({
    campaignType,
    errors,
    register,
    currencySymbol,
    fileUrl,
    onReplaceFile,
}: BriefSectionProps) {
    const briefFileId = useId();

    return (
        <section id="brief" className="scroll-mt-16">
            <Heading type="h4" className="mb-4 font-bold">
                Brief
            </Heading>
            <Card size="sm">
                <CardContent>
                    <FieldGroup className="gap-6">
                        {campaignType === "Song" ? (
                            <FormInput
                                label="Sound URL"
                                required
                                type="text"
                                placeholder="https://tiktok.com/music/123455"
                                error={
                                    errors.soundUrl
                                        ? errors.soundUrl.message
                                            ? (
                                                <span>
                                                    Use:&nbsp;&nbsp;
                                                    <strong>https://www.tiktok.com/music/...</strong>
                                                    &nbsp;&nbsp;or&nbsp;&nbsp;
                                                    <strong>https://vm.tiktok.com/...</strong>
                                                    &nbsp;&nbsp;or&nbsp;&nbsp;
                                                    <strong>https://www.instagram.com/reels/audio/...</strong>
                                                    &nbsp;&nbsp;or&nbsp;&nbsp;
                                                    <strong>https://www.youtube.com/source/...</strong>
                                                </span>
                                            )
                                            : "Sound URL is required."
                                        : undefined
                                }
                                {...register("soundUrl", {
                                    required: campaignType === "Song",
                                    setValueAs: (val) => {
                                        try {
                                            return decodeURI(val);
                                        } catch {
                                            return val;
                                        }
                                    },
                                    pattern: {
                                        value: SOUND_URL_PATTERN,
                                        message: "Valid URLs start with https://www.tiktok.com/music/ OR https://vm.tiktok.com/ OR https://www.youtube.com/source/",
                                    },
                                })}
                            />
                        ) : null}

                        {/* Min/max are a two-column budget row — margins cannot place them side by side. */}
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <FormFieldShell
                                label="Min Price per Creator"
                                htmlFor="min-price"
                                invalid={Boolean(errors.minPricePerCreator)}
                                error={errors.minPricePerCreator?.message}
                            >
                                {(controlProps) => (
                                    <InputGroup>
                                        <InputGroupAddon>
                                            <InputGroupText>{currencySymbol}</InputGroupText>
                                        </InputGroupAddon>
                                        <InputGroupInput
                                            {...controlProps}
                                            type="number"
                                            min={0}
                                            step="1"
                                            placeholder="0"
                                            {...register("minPricePerCreator", {
                                                setValueAs: optionalPrice,
                                                validate: priceIsOptionalOrNonNegative,
                                            })}
                                        />
                                    </InputGroup>
                                )}
                            </FormFieldShell>
                            <FormFieldShell
                                label="Max Price per Creator"
                                htmlFor="max-price"
                                invalid={Boolean(errors.maxPricePerCreator)}
                                error={errors.maxPricePerCreator?.message}
                            >
                                {(controlProps) => (
                                    <InputGroup>
                                        <InputGroupAddon>
                                            <InputGroupText>{currencySymbol}</InputGroupText>
                                        </InputGroupAddon>
                                        <InputGroupInput
                                            {...controlProps}
                                            type="number"
                                            min={0}
                                            step="1"
                                            placeholder="5,000"
                                            {...register("maxPricePerCreator", {
                                                setValueAs: optionalPrice,
                                                validate: priceIsOptionalOrNonNegative,
                                            })}
                                        />
                                    </InputGroup>
                                )}
                            </FormFieldShell>
                        </div>

                        <FormInput
                            label="Deliverables"
                            placeholder="2x TikTok, 1x stories"
                            {...register("deliverables")}
                        />

                        <FormTextarea
                            label="Campaign Details & Timeline"
                            required
                            placeholder="Number of creators needed, campaign details, timeline, etc."
                            error={
                                errors.details
                                    ? "Campaign details are required."
                                    : undefined
                            }
                            {...register("details", { required: true })}
                        />

                        {fileUrl ? (
                            <FormFieldShell label="Brief File (optional)" htmlFor={briefFileId}>
                                {() => (
                                    // Link and replace action share one row.
                                    <div className="flex items-center gap-4">
                                        <TextLink
                                            id={briefFileId}
                                            href={fileUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Existing File
                                        </TextLink>
                                        <Button
                                            type="button"
                                            onClick={onReplaceFile}
                                            variant="destructive"
                                        >
                                            <Trash2Icon />
                                            Replace File
                                        </Button>
                                    </div>
                                )}
                            </FormFieldShell>
                        ) : (
                            <FormInput
                                label="Brief File (optional)"
                                type="file"
                                accept="image/*,.pdf,.doc,.docx"
                                {...register("file")}
                            />
                        )}
                    </FieldGroup>
                </CardContent>
            </Card>
        </section>
    );
}
